import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import type { MonitorTabId } from '../constants/question.constants';
import { toMonitorFilterSearch, toMonitorFilterValues } from '../utils/questionFilters';

const ROUTES = {
  download: getRouteApi('/_auth/question/download'),
  byBoard: getRouteApi('/_auth/question/download-by-board'),
  byClass: getRouteApi('/_auth/question/download-by-class'),
} as const;

export type MonitorRouteId = keyof typeof ROUTES;

const normalizeFilterValues = (values: Record<string, string>) =>
  toMonitorFilterValues(toMonitorFilterSearch(values));

export function useMonitorFilters(routeId: MonitorRouteId) {
  const route = ROUTES[routeId];
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedValues = useMemo(() => toMonitorFilterValues(search), [search]);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toMonitorFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const navigateToProject = (projectId: number) =>
    navigate({
      search: (previous) => ({ ...previous, projectId }),
      replace: true,
    });

  const changeTab = (tab: MonitorTabId) =>
    navigate({
      search: (previous) => ({ ...previous, tab }),
      replace: true,
    });

  const searchBox = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, search: text }),
        replace: true,
      });
    },
  });

  const { tab, ...params } = search;

  return {
    ...filters,
    ...searchBox,
    params,
    activeTabId: tab ?? 'summary',
    changeTab,
    navigateToProject,
  };
}
